import './mortgage.css';
import { can_mortgage_payment, cmhc_premium } from './canada-mortgage-js.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatNumber } from '../../../../util/format-number';
import { iconDollar } from '../../../../util/icons';

const DEFAULT_MORTGAGE_DOWN_PAYMENT_PERCENT = '20';

export interface MortgageProps {
  address: string;
  price: number;
}

function calculateDownPayment(price: number, downPaymentPercent: number) {
  return downPaymentPercent < 100 ? price * downPaymentPercent / 100 : price;
}

function calculateDownPaymentPercent(price: number, downPayment: number) {
  return downPayment < price ? price * downPayment / 100 : 100;
}

const createFloat = (val: string) => {
  return parseFloat(val.replace(/,/g, ''));
}

const MORTGAGE_AMORTIZATION_OPTIONS = [
  '5', '10', '15', '20', '25', '30'
];

const DOWN_PAYMENT_PERCENT_OPTIONS = [
  '5', '10', '15', '20', '25', '30', '35', '40', '45', '50'
];

const MORTGAGE_RATES = [
  '1.49', '1.99', '2.49', '2.99', '3.49', '3.99', '4.49', '4.99', '5.49', '5.99', '6.49', '6.99', '7.49', '7.99', '8.49', '8.99', '9.49', '9.99'
];

const MORTGAGE_FREQUENCY_OPTIONS = [
  'Monthly', 'Bi-weekly'
];

export const Mortgage: React.FC<MortgageProps> = (props: MortgageProps) => {

  const [mortgagePayment, setMortgagePayment] = useState<number>(0);
  const [mortgagePaymentInputValue, setMortgagePaymentInputValue] = useState('0');
  const [downPaymentAmount, setDownPaymentAmount] = useState(calculateDownPayment(props.price, parseFloat(DEFAULT_MORTGAGE_DOWN_PAYMENT_PERCENT)));
  const [downPaymentAmountInputValue, setDownPaymentAmountInputValue] = useState(formatNumber(calculateDownPayment(props.price, parseFloat(DEFAULT_MORTGAGE_DOWN_PAYMENT_PERCENT))));
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [downPaymentPercentInputValue, setDownPaymentPercentInputValue] = useState(DEFAULT_MORTGAGE_DOWN_PAYMENT_PERCENT);
  const [rate, setRate] = useState('4.99');
  const [amortization, setAmortization] = useState('25');
  const [mortgageInsuranceAmount, setMortgageInsuranceAmount] = useState<number>(0);
  const [mortgageInsuranceAmountInputValue, setMortgageInsuranceAmountInputValue] = useState<string>('0');
  const [totalMortgageAmount, setTotalMortgageAmount] = useState<number>(0);
  const [totalMortgageAmountInputValue, setTotalMortgageAmountInputValue] = useState<string>('0');
  const [alert, setAlert] = useState<string | null>(null);

  const [frequency, setFrequency] = useState('Monthly');
  const [isDpInputFocused, setIsDpInputFocused] = useState(false);

  const listPriceInputValue = useMemo(() => formatNumber(props.price), [props.price]);

  const frequencyText = useMemo(() => frequency === 'Bi-weekly' ? '/ bi-weekly' : '/ month', [frequency]);

  const setDownPaymentAmountManually = useCallback((val: string) => {
    const strippedVal = val.replace(/,/g, '');
    const floatVal = strippedVal.length === 0 ? 0 : createFloat(strippedVal);
    const dpPercent = floatVal / props.price * 100;
    setDownPaymentAmount(floatVal);
    setDownPaymentPercent(calculateDownPaymentPercent(props.price, floatVal));
    setDownPaymentPercentInputValue(dpPercent.toFixed(2));
  }, [props.price]);

  const setDownPaymentPercentManually = useCallback((val: string) => {
    const strippedVal = val.replace(/,/g, '');
    const floatVal = strippedVal.length === 0 ? 0 : createFloat(strippedVal);
    const dpAmount = calculateDownPayment(props.price, floatVal);
    setDownPaymentPercent(floatVal);
    setDownPaymentPercentInputValue(formatNumber(floatVal));
    setDownPaymentAmount(dpAmount);
  }, [props.price]);

  useEffect(() => {
    !isDpInputFocused &&
      setDownPaymentAmountInputValue(formatNumber(downPaymentAmount));
  }, [downPaymentAmount, isDpInputFocused]);

  useEffect(() => {
    // find mortgage insurance costs
    const loanToValue = 1 - (downPaymentAmount / props.price);
    let tmpMortgageInsurance = 0;
    let tmpTotalMortgage = 0;

    if (loanToValue > .95) {
      setAlert('Mortgage insurance providers require a mininum of 5% down payment.');
    } else {
      setAlert(null);
    }
    if (downPaymentAmount < props.price) {
      const cmhcPremium = cmhc_premium(loanToValue);
      if (cmhcPremium !== undefined && cmhcPremium > 0) {
        tmpMortgageInsurance = props.price * cmhcPremium;
      }
      tmpTotalMortgage = props.price - downPaymentAmount + tmpMortgageInsurance;
    }
    setMortgageInsuranceAmount(tmpMortgageInsurance);
    setTotalMortgageAmount(tmpTotalMortgage);
  }, [props.price, downPaymentAmount]);

  useEffect(() => {
    setMortgageInsuranceAmountInputValue(formatNumber(mortgageInsuranceAmount));
  }, [mortgageInsuranceAmount]);

  useEffect(() => {
    setTotalMortgageAmountInputValue(formatNumber(totalMortgageAmount));
  }, [totalMortgageAmount]);

  useEffect(() => {
    let tmpPayment = 0;
    tmpPayment = can_mortgage_payment(totalMortgageAmount, Number(rate) / 100, amortization, frequency === 'Bi-weekly' ? 26 : 12, 1);
    setMortgagePayment(tmpPayment);
  }, [amortization, frequency, rate, totalMortgageAmount]);

  useEffect(() => {
    setMortgagePaymentInputValue(formatNumber(mortgagePayment) + ' ' + frequencyText);
  }, [frequencyText, mortgagePayment]);


  return (
    <div id="mortgage" className="mortgage">
      <h2 className="title">Mortgage Payment</h2>
      <div>Estimated mortgage payment for <strong>{props.address}</strong> at the list price of <strong>${listPriceInputValue}</strong>, <strong>{downPaymentPercentInputValue}%</strong> down, and a mortgage rate of <strong>{rate}%</strong> is <strong>${mortgagePaymentInputValue}</strong>.</div>

      <div className="options-div results-div">
        <div className="list-price">
          <label>List Price</label>
          <div className="form-field-with-icon">
            <div className="input-icon">
              {iconDollar}
            </div>
            <div>
              <input disabled value={listPriceInputValue} type="text" name="list-price" className="square-left-corners" />
            </div>
          </div>
        </div>
      </div>

      <div className="options-div">
        <div className="down-payment-div">
          <label>Down Payment</label>
          <div className="down-payment-input-div">
            <div className="form-field-with-icon">
              <div className="input-icon">
                {iconDollar}
              </div>
              <div>
                <input onBlur={() => setIsDpInputFocused(false)} onFocus={() => setIsDpInputFocused(true)} onChange={e => setDownPaymentAmountManually(e.target.value)} value={isDpInputFocused ? downPaymentAmount.toString() : downPaymentAmountInputValue} inputMode='numeric' type="text" name="down-payment-amount" className="down-payment-amount-input square-left-corners square-right-corners" />
                <select value={downPaymentPercent} onChange={e => setDownPaymentPercentManually(e.target.value)} name="down-payment-percent" className="down-payment-percent-input square-left-corners">
                  {!DOWN_PAYMENT_PERCENT_OPTIONS.includes(downPaymentPercentInputValue) &&
                    <option value={downPaymentPercent}>{downPaymentPercentInputValue} %</option>
                  }
                  {
                    DOWN_PAYMENT_PERCENT_OPTIONS.map(dp =>
                      <option key={dp} value={dp}>{dp} %</option>
                    )
                  }
                </select>
              </div>
            </div>
          </div>
        </div>
        <div>
          <label>Rate</label>
          <div>
            <select value={rate} onChange={e => setRate(e.target.value)} name="rate" className="rate-input">
              {
                MORTGAGE_RATES.map(mr =>
                  <option key={mr} value={mr}>{mr} %</option>
                )
              }
            </select>
          </div>
        </div>
        <div>
          <label>Amortization</label>
          <div>
            <select value={amortization} onChange={e => setAmortization(e.target.value)} name="amortization" className="amortization-input">
              {
                MORTGAGE_AMORTIZATION_OPTIONS.map(ay =>
                  <option key={ay} value={ay}>{ay} Years</option>
                )
              }
            </select>
          </div>
        </div>
        <div>
          <label>Frequency</label>
          <div>
            <select value={frequency} onChange={e => setFrequency(e.target.value)} name="frequency" className="frequecy-input">
              {
                MORTGAGE_FREQUENCY_OPTIONS.map(mf =>
                  <option key={mf} value={mf}>{mf}</option>
                )
              }
            </select>
          </div>
        </div>
      </div>
      {
        alert !== null &&
        <div className="alert">{alert}</div>
      }
      <div className="options-div results-div">
        <div className="mortgage-insurance">
          <label>Mortgage Insurance</label>
          <div className="form-field-with-icon">
            <div className="input-icon">
              {iconDollar}
            </div>
            <div>
              <input disabled value={mortgageInsuranceAmountInputValue} type="text" name="mortgage-insurance-amount" className="square-left-corners" />
            </div>
          </div>
        </div>
        <div className="total-mortgage">
          <label>Total Mortgage</label>
          <div className="form-field-with-icon">
            <div className="input-icon">
              {iconDollar}
            </div>
            <div>
              <input disabled value={totalMortgageAmountInputValue} type="text" name="total-mortgage-amount" className="square-left-corners" />
            </div>
          </div>
        </div>
        <div className="mortgage-payment">
          <label>Mortgage Payment</label>
          <div className="form-field-with-icon">
            <div className="input-icon">
              {iconDollar}
            </div>
            <div>
              <input disabled value={mortgagePaymentInputValue} type="text" name="mortgage-payment-amount" className="square-left-corners" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
